import { io } from 'socket.io-client';

/**
 * VoiceTestClient
 * Socket.IO client for voice AI test sessions.
 * Connects to the /voice-test namespace — text-based, no audio.
 */
export class VoiceTestClient {
  constructor() {
    this.socket = null;
    this.handlers = {};
  }

  /**
   * Connect to the voice test server
   * @param {object} options
   * @param {string} options.token - JWT token (staff auth)
   * @param {string} [options.url] - Server URL
   */
  connect({ token, url } = {}) {
    // In dev, CRA runs on :3000 but the API/Socket.IO server is on :3001
    const serverUrl = url || window.location.origin.replace(':3000', ':3001');

    this.socket = io(`${serverUrl}/voice-test`, {
      auth: { token },
      transports: ['websocket'],
      reconnection: false
    });

    const events = [
      'sessionReady', 'transcript', 'toolCall', 'toolResult',
      'turnComplete', 'scenarioProgress', 'scenarioComplete',
      'autoTestProgress', 'autoTestComplete',
      'sessionEnded', 'error', 'connect_error'
    ];

    for (const event of events) {
      this.socket.on(event, (data) => {
        if (this.handlers[event]) {
          this.handlers[event](data);
        }
      });
    }

    this.socket.on('connect', () => {
      console.log('[VoiceTest] Connected');
      if (this.handlers.connect) this.handlers.connect();
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[VoiceTest] Disconnected:', reason);
      if (this.handlers.disconnect) this.handlers.disconnect(reason);
    });
  }

  on(event, handler) {
    this.handlers[event] = handler;
    return this;
  }

  startSession({ practiceId, campaignType, mode }) {
    if (!this.socket) throw new Error('Not connected');
    this.socket.emit('startTestSession', { practiceId, campaignType, mode });
  }

  sendMessage(text) {
    if (!this.socket) throw new Error('Not connected');
    this.socket.emit('sendMessage', { text });
  }

  runScenario({ scenarioName, practiceId, campaignType }) {
    if (!this.socket) throw new Error('Not connected');
    this.socket.emit('runScenario', { scenarioName, practiceId, campaignType });
  }

  runAutoTest({ practiceId, scenarioName, mode, campaignType }) {
    if (!this.socket) throw new Error('Not connected');
    this.socket.emit('runAutoTest', { practiceId, scenarioName, mode, campaignType });
  }

  endSession() {
    if (!this.socket) return;
    this.socket.emit('endTestSession');
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.handlers = {};
  }

  get isConnected() {
    return this.socket?.connected ?? false;
  }
}

export default VoiceTestClient;
